import type { FilterOption, FilterOptionConfig } from '@brightdrop/bd-ui';
import type { PricingModelDto } from '@gm-commercial/entitlements-client/src/api/entitlementsApi';

import type { BDFilterModalOptionConfig } from '~/common/components/BDFilter/BDFilterModal';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import { BillingCycleDuration } from '~/common/models/plans.model';
import { formatCurrency } from '~/common/utils/number.utils';
import type {
  EligibleVehiclesFilterCriteria,
  FleetInfo,
} from './eligibleVehiclesFilter.model';

// Rate filter
const RATE_TITLE_KEYS: Record<BillingCycleDuration, string> = {
  [BillingCycleDuration.MONTHLY]:
    'entitlements:planDetails.columnHeaders.monthlyRate',
  [BillingCycleDuration.YEARLY]:
    'entitlements:planDetails.columnHeaders.yearlyRate',
};

export const getPlanRateFilterOptions = (
  pricingModels: PricingModelDto[],
  formatMoney?: (value: number) => string
): FilterOption<string>[] => {
  const formatPrice = formatMoney ?? formatCurrency;

  return pricingModels.map((model) => ({
    label: formatPrice(model.price) || DEFAULT_EMPTY_VALUE,
    value: model.id,
  }));
};

const planRateFilter = (
  billingCycleDuration: BillingCycleDuration,
  pricingModels: PricingModelDto[],
  formatMoney?: (value: number) => string
): FilterOptionConfig<string, EligibleVehiclesFilterCriteria> => ({
  title: translateText(RATE_TITLE_KEYS[billingCycleDuration]),
  id: 'pricePlan-rate',
  type: 'pricingModels',
  options: getPlanRateFilterOptions(pricingModels, formatMoney),
});

export const getEligibleVehiclesFilterConfig = (
  billingCycleDuration: BillingCycleDuration,
  pricingModels: PricingModelDto[],
  fleets: FleetInfo[],
  formatMoney?: (value: number) => string
): BDFilterModalOptionConfig<EligibleVehiclesFilterCriteria>[] => [
  planRateFilter(billingCycleDuration, pricingModels, formatMoney),
  fleetFilter(fleets),
];

// Fleet filter
export const getFleetFilterOptions = (
  fleets: FleetInfo[]
): FilterOption<string>[] =>
  fleets
    ?.filter((fleet) => fleet.fleetId !== DEFAULT_EMPTY_VALUE)
    ?.map((fleet) => ({
      label: fleet.name,
      value: fleet.fleetId,
    }));

const fleetFilter = (
  fleets: FleetInfo[]
): FilterOptionConfig<string, EligibleVehiclesFilterCriteria> => ({
  title: translateText('common:fleet'),
  id: 'fleet-filter',
  type: 'fleetIds',
  options: getFleetFilterOptions(fleets),
});

export const getFleetFilterConfig = (
  fleets: FleetInfo[]
): BDFilterModalOptionConfig<EligibleVehiclesFilterCriteria>[] => [
  fleetFilter(fleets),
];
